import React, { useContext, useMemo, useState } from "react";
import { ContextEngine } from "../../engine/ContextEngine";
import "./Right1.css";

const Right = () => {
  const { context, updateContext } = useContext(ContextEngine);
  const [bulkKey, setBulkKey] = useState("");
  const [bulkValue, setBulkValue] = useState("");
  const [isActionsExpanded, setIsActionsExpanded] = useState(true);
  const [editTarget, setEditTarget] = useState("meta"); 

  const tableId = "defaultTable";
  const unitIndex = context.context_units?.findIndex(u => u.id === tableId);
  const table = context.context_units?.[unitIndex];

  // 1. Memoize Selected Items & Indices
  const selectedIndices = useMemo(() => {
    if (!table?.data) return [];
    return table.data
      .map((item, idx) => (item.meta?.selected ? idx : -1))
      .filter(idx => idx !== -1);
  }, [table]);

  const selectedItems = useMemo(() => {
    return selectedIndices.map(idx => table.data[idx]);
  }, [selectedIndices, table]);

  // 2. BULK UPDATE HANDLER
// 2. BULK UPDATE HANDLER (Optimised)
const handleBulkUpdate = () => {
  if (!bulkKey || !bulkValue || selectedIndices.length === 0) return;

  // 1. Create a deep copy of the current table data
  const newData = [...table.data];

  // 2. Apply all changes to the local copy in memory
  selectedIndices.forEach(idx => {
    // Ensure the target (meta or row) exists
    const target = { ...(newData[idx][editTarget] || {}) };
    target[bulkKey] = bulkValue;
    
    // Update the row object at that index
    newData[idx] = {
      ...newData[idx],
      [editTarget]: target
    };
  });

  // 3. Send the entire updated array to Context in ONE call
  const dataPath = `context_units.${unitIndex}.data`;
  updateContext(dataPath, newData);

  console.log(`Bulk updated ${selectedIndices.length} rows at once.`);
  setBulkValue(""); 
  setBulkKey("");
};


  // 3. EXPORT LOGIC
  const exportToCSV = () => {
    if (!selectedItems.length) return;
    const firstRowKeys = selectedItems[0]?.row ? Object.keys(selectedItems[0].row) : [];
    const headers = ["row_id", ...firstRowKeys, "metadata"];
    
    const csvRows = selectedItems.map(item => [
      item.row_id,
      ...Object.values(item.row || {}),
      JSON.stringify(item.meta || {}).replace(/,/g, ';') 
    ]);

    const csvContent = [headers, ...csvRows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `bulk_export_${new Date().getTime()}.csv`;
    link.click();
  };

  // 4. DELETE LOGIC
  const handleDeleteSelected = () => {
    if (!selectedItems.length || !window.confirm(`Are you sure you want to delete ${selectedItems.length} rows?`)) {
      return;
    }
    const updatedData = table.data.filter(item => item.meta?.selected !== true);
    const dataPath = `context_units.${unitIndex}.data`;
    updateContext(dataPath, updatedData);
  };

  if (!context.panelVisibility?.right) return null;

  return (
    <div className="right-panel" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      
      {/* HEADER SECTION */}
      <div className="panel-header" style={{ 
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', 
        padding: '15px 20px', borderBottom: '1px solid #ddd', gap: '4px', flexShrink: 0 
      }}>
        <h4 style={{ margin: 0, fontSize: '16px' }}>Inspector</h4>
        <div style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>
          {selectedItems.length} rows selected
        </div>
      </div>

      {selectedItems.length > 0 && (
        <div className="action-zone" style={{ borderBottom: '1px solid #ddd', flexShrink: 0 }}>
          
          {/* 1. COLLAPSIBLE TRIGGER */}
        <div 
          onClick={() => setIsActionsExpanded(!isActionsExpanded)}
          style={{
            padding: '10px 15px', 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center', 
            cursor: 'pointer', 
            /* DARK THEME: Deep grey background with a subtle top border */
            background: '#1a1a1a', 
            borderTop: '1px solid var(--border-color)',
            borderBottom: isActionsExpanded ? 'none' : '1px solid var(--border-color)',
            fontSize: '10px', 
            letterSpacing: '0.5px',
            fontWeight: '800', 
            color: 'var(--text-dim)', /* Muted text */
            transition: 'background 0.2s ease'
          }}
          /* Optional: Subtle hover effect */
          onMouseEnter={(e) => e.currentTarget.style.background = '#222'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#1a1a1a'}
        >
          <span style={{ color: isActionsExpanded ? 'var(--text-main)' : 'var(--text-dim)' }}>
            {isActionsExpanded ? "▼ HIDE ACTIONS" : "▲ SHOW ACTIONS"}
          </span>
          
          <span style={{ 
            color: 'var(--accent)', 
            fontSize: '11px',
            opacity: isActionsExpanded ? 0.6 : 1,
            fontWeight: 'bold'
          }}>
            {isActionsExpanded ? "" : `(${selectedItems.length} READY)`}
          </span>
        </div>


          {/* 2. EXPANDABLE CONTENT */}
          {isActionsExpanded && (
            <div className="action-content" style={{ padding: '10px', background: 'var(--bg-panel)', borderBottom: '1px solid var(--border-color)' }}>
              
              {/* 1. TARGET TOGGLE (THEME: NEUTRE DEEP) */}
              <div style={{ display: 'flex', marginBottom: '10px', background: '#0a0a0a', borderRadius: '4px', padding: '2px', border: '1px solid #333' }}>
                <button 
                  onClick={() => setEditTarget("meta")}
                  style={{ 
                    flex: 1, border: 'none', padding: '6px', fontSize: '10px', borderRadius: '3px', cursor: 'pointer', 
                    background: editTarget === "meta" ? "#333" : "transparent", 
                    color: editTarget === "meta" ? "var(--accent)" : "#888",
                    fontWeight: 'bold', transition: '0.2s' 
                  }}
                >META</button>
                <button 
                  onClick={() => setEditTarget("row")}
                  style={{ 
                    flex: 1, border: 'none', padding: '6px', fontSize: '10px', borderRadius: '3px', cursor: 'pointer', 
                    background: editTarget === "row" ? "#333" : "transparent", 
                    color: editTarget === "row" ? "#e67e22" : "#888",
                    fontWeight: 'bold', transition: '0.2s' 
                  }}
                >ROW DATA</button>
              </div>

              {/* 2. BULK INPUTS (THEME: GLASS-INPUT) */}
              <div className="bulk-edit-section">
                <input 
                  className="bulk-input" 
                  placeholder={editTarget === "meta" ? "Meta Key (status...)" : "Column Name (Category...)"}
                  value={bulkKey} 
                  onChange={e => setBulkKey(e.target.value)} 
                  style={{ 
                    width: '100%', marginBottom: '4px', padding: '8px', fontSize: '12px', 
                    background: '#111', border: '1px solid #444', borderRadius: '4px', color: '#eee' 
                  }}
                />
                <input 
                  className="bulk-input" 
                  placeholder="New Value" 
                  value={bulkValue} 
                  onChange={e => setBulkValue(e.target.value)} 
                  style={{ 
                    width: '100%', marginBottom: '10px', padding: '8px', fontSize: '12px', 
                    background: '#111', border: '1px solid #444', borderRadius: '4px', color: '#eee' 
                  }}
                />
                
                {/* UPDATE BUTTON (DYNAMIC COLOR) */}
                <button 
                  onClick={handleBulkUpdate} 
                  style={{ 
                    width: '100%', 
                    backgroundColor: editTarget === "row" ? "#d35400" : "var(--accent)", 
                    color: 'white', border: 'none', padding: '10px', borderRadius: '4px', 
                    cursor: 'pointer', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' 
                  }}
                >
                  Update {editTarget} ({selectedItems.length})
                </button>
              </div>

              {/* 3. UTILITY BUTTONS (MUTED TONES) */}
              <div className="utility-section" style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
                <button 
                  onClick={exportToCSV} 
                  style={{ flex: 1, padding: '8px', cursor: 'pointer', background: '#1b5e20', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '11px' }}
                >CSV</button>
                <button 
                  onClick={handleDeleteSelected} 
                  style={{ flex: 1, padding: '8px', cursor: 'pointer', background: '#b71c1c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '11px' }}
                >Delete</button>
              </div>
            </div>
          )
          }
        </div>
      )}

      {/* 3. DOCK FILL CONTENT: Scrollable Metadata List */}
      <div className="panel-content" style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
        {selectedItems.length > 0 ? (
          selectedItems.map((item) => (
          /* --- Inside your selectedItems.map --- */
          <div key={item.row_id} className="inspector-card">
            
            {/* ID Badge */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontSize: '10px', color: '#666' }}>ID: {item.row_id}</span>
              <span style={{ fontSize: '10px', background: '#333', padding: '2px 6px', borderRadius: '10px' }}>
                UNIT: {tableId}
              </span>
            </div>

            {/* 1. ROW DATA SECTION */}
            <div className="card-section">
              <div className="card-section-label row-data-label">Row Data</div>
              <div className="row-data-box">
                <pre className="code-block">
                  {JSON.stringify(item.row, null, 2)}
                </pre>
              </div>
            </div>

            {/* 2. METADATA SECTION */}
            <div className="card-section">
              <div className="card-section-label meta-data-label">Metadata</div>
              <div className="meta-data-box">
                <pre className="code-block">
                  {JSON.stringify(item.meta, null, 2)}
                </pre>
              </div>
            </div>
          </div>

          ))
        ) : (
          <div className="empty-state" style={{ textAlign: 'center', color: '#999', paddingTop: '40px' }}>
            Select rows in the table to inspect.
          </div>
        )}
      </div>
    </div>
  );
};

export default Right;
